import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Button } from '~/ui/button'
import { Badge } from '~/ui/badge'
import { PlaybackAnalyticsDashboard } from './components/playback-analytics-dashboard'
import { PlaybackEventsList } from './components/playback-events-list'
import { LocationAnalyticsDashboard } from './components/location-analytics-dashboard'
import { RoyaltyReportingDashboard } from './components/royalty-reporting-dashboard'
import { useCurrentOrganization } from '~/auth/use-current-organization'
import {
  BarChart3,
  List,
  Download,
  Filter,
  TrendingUp,
  Users,
  MapPin,
  Globe,
  BadgeDollarSign,
} from 'lucide-react'

const ZONES_QUERY = gql(`
  query ZonesForReporting($organizationId: ID) {
    zones(organizationId: $organizationId) {
      edges {
        node {
          id
          name
          location {
            id
            name
          }
          devices {
            id
            code
            status
          }
        }
      }
    }
  }
`)

interface ReportingScreenProps {
  className?: string
}

const isOnlineStatus = (status?: string) => ['online', 'playing', 'paused'].includes(status || '')

export const ReportingScreen: React.FC<ReportingScreenProps> = ({ className = '' }) => {
  const [selectedZone, setSelectedZone] = useState<string>('all')
  const [activeTab, setActiveTab] = useState('analytics')
  const { id: organizationId, organization } = useCurrentOrganization()

  const { data: zonesData } = useQuery(ZONES_QUERY, {
    variables: { organizationId: organizationId ?? undefined },
    skip: !organizationId,
  })
  const zones = zonesData?.zones?.edges || []
  const selectedZoneData = zones.find((zone) => zone.node?.id === selectedZone)

  const handleExportData = async () => {
    const zonesToExport =
      selectedZone === 'all'
        ? zones.map((zone) => zone.node).filter(Boolean)
        : selectedZoneData?.node
          ? [selectedZoneData.node]
          : []

    if (zonesToExport.length === 0) return

    const escapeCsv = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`

    const header = ['Zone ID', 'Zone Name', 'Location', 'Online Devices', 'Total Devices', 'Status']
    const rows = zonesToExport.map((zone: any) => {
      const summary = getZoneStatusSummary(zone)
      return [
        zone.id ?? '',
        zone.name ?? '',
        zone.location?.name ?? '',
        summary.onlineDevices,
        summary.totalDevices,
        summary.status,
      ]
    })

    const csv = [header, ...rows]
      .map((row) => row.map((value) => escapeCsv(value)).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const datePart = new Date().toISOString().slice(0, 10)
    const scopePart = selectedZone === 'all' ? 'all-zones' : selectedZone

    link.href = url
    link.setAttribute('download', `playback-report-${scopePart}-${datePart}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const getZoneStatusSummary = (zone: any) => {
    const onlineDevices =
      zone.devices?.filter((device: any) => isOnlineStatus(device.status))?.length || 0
    const totalDevices = zone.devices?.length || 0

    return {
      onlineDevices,
      totalDevices,
      status: onlineDevices > 0 ? 'active' : 'inactive',
    }
  }

  return (
    <div className={`flex h-screen flex-col ${className}`}>
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold">Playback Reporting</h1>
            <p className="mt-1 text-gray-600">
              Track playback analytics and events across your zones
            </p>
            {organization && (
              <Badge variant="outline" className="mt-2">
                {organization.name}
              </Badge>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleExportData}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Zone Filter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Zone Filter
            </CardTitle>
            <CardDescription>
              Select a zone to view specific analytics, or choose "All Zones" for organization-wide
              data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="min-w-64 flex-1">
                <Select value={selectedZone} onValueChange={setSelectedZone}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Zones</SelectItem>
                    {zones.map((zone: any) => {
                      const summary = getZoneStatusSummary(zone.node)
                      return (
                        <SelectItem key={zone.node?.id} value={zone.node?.id ?? ''}>
                          <div className="flex w-full items-center justify-between">
                            <span>{zone.node?.name}</span>
                            <div className="ml-2 flex items-center gap-2">
                              <Badge
                                variant={summary.status === 'active' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {summary.onlineDevices}/{summary.totalDevices}
                              </Badge>
                            </div>
                          </div>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedZoneData && (
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {selectedZoneData.node?.location?.name}
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {getZoneStatusSummary(selectedZoneData).onlineDevices} online
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Zone Overview Cards */}
        {selectedZone === 'all' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {zones.map((zone: any) => {
              const summary = getZoneStatusSummary(zone.node)
              return (
                <Card key={zone.node?.id} className="transition-shadow hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{zone.node?.name}</CardTitle>
                      <Badge variant={summary.status === 'active' ? 'default' : 'secondary'}>
                        {summary.status}
                      </Badge>
                    </div>
                    <CardDescription>{zone.node?.location?.name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Devices:</span>
                        <span>
                          {summary.onlineDevices}/{summary.totalDevices} online
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => (zone.node ? setSelectedZone(zone.node?.id) : null)}
                        className="w-full"
                      >
                        View Analytics
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col space-y-4"
        >
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics Dashboard
            </TabsTrigger>
            <TabsTrigger value="location" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Location Analytics
            </TabsTrigger>
            <TabsTrigger value="royalties" className="flex items-center gap-2">
              <BadgeDollarSign className="h-4 w-4" />
              Royalty Reporting
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Event History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="flex-1 space-y-4 overflow-y-auto">
            <PlaybackAnalyticsDashboard
              organizationId={organizationId ?? undefined}
              zoneId={selectedZone !== 'all' ? selectedZone : undefined}
              zoneName={selectedZoneData?.node?.name}
            />
          </TabsContent>

          <TabsContent value="location" className="flex-1 space-y-4 overflow-y-auto">
            <LocationAnalyticsDashboard
              organizationId={organizationId ?? undefined}
              zoneId={selectedZone !== 'all' ? selectedZone : undefined}
              zoneName={selectedZoneData?.node?.name}
            />
          </TabsContent>

          <TabsContent value="royalties" className="flex-1 space-y-4 overflow-y-auto">
            <RoyaltyReportingDashboard
              organizationId={organizationId ?? undefined}
              zoneId={selectedZone !== 'all' ? selectedZone : undefined}
              locationId={selectedZoneData?.node?.location?.id}
              zoneName={selectedZoneData?.node?.name}
            />
          </TabsContent>

          <TabsContent value="events" className="flex-1 space-y-4 overflow-y-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <List className="h-5 w-5" />
                  Playback Events
                </CardTitle>
                <CardDescription>
                  Detailed log of all playback events
                  {selectedZoneData ? ` for ${selectedZoneData?.node?.name}` : ' across all zones'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlaybackEventsList
                  organizationId={organizationId ?? undefined}
                  zoneId={selectedZone !== 'all' ? selectedZone : undefined}
                  defaultLimit={100}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Quick Stats Footer */}
      <Card className="m-6 mt-0 flex-shrink-0 bg-gray-50 p-6">
        <CardContent className="mb-6">
          <div className="grid grid-cols-1 gap-4 text-center md:grid-cols-3">
            <div className="flex items-center justify-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{zones.length}</div>
                <div className="text-sm text-gray-600">Total Zones</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold">
                  {zones.reduce(
                    (acc: number, zone: any) => acc + (zone.node?.devices?.length || 0),
                    0
                  )}
                </div>
                <div className="text-sm text-gray-600">Total Devices</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <div>
                <div className="text-2xl font-bold">
                  {zones.reduce((acc: number, zone: any) => {
                    const summary = getZoneStatusSummary(zone.node)
                    return acc + summary.onlineDevices
                  }, 0)}
                </div>
                <div className="text-sm text-gray-600">Online Now</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
